import { Dispatch, SetStateAction, useState } from "react"
import { DollarSign, Calculator, CheckCircle2 } from "lucide-react"
import { Button } from "@components/common/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import { Label } from "@components/common/label"
import { Input } from "@components/common/input"
import { Textarea } from "@components/common/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { Alert, AlertDescription, AlertTitle } from "@components/common/alert"
import { DictType } from "@pma/types"
import { formatCurrency } from "@pma/utils/utils"
import useTranslator from "@pma/hooks/use-translator"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { BILL_DIARY_LINK, EDIT_BILL_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { toast } from "@pma/hooks/use-toast"
import { useMutation } from "@tanstack/react-query"
import { Badge } from "@components/common/badge"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { Checkbox } from "@components/common/checkbox"
import { useNavigate, useSearchParams } from "react-router-dom"
import SelectWorkDiaryList from "./select-work-diary-list"
import BoqTablePreview from "./boq-table-preview"

interface EditBillDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedBills: Array<{ id: string; name: string; amount: string; project: string }>
  setSelectedItems: Dispatch<SetStateAction<Array<any>>>
  onAdjust: CallableFunction
  bill: DictType
  sendToApproval?: boolean
}

export function EditBillDialog({
  open,
  onOpenChange,
  selectedBills,
  onAdjust,
  bill,
  sendToApproval,
  setSelectedItems,
}: EditBillDialogProps) {
  const [reason, setReason] = useState("")
  const [selectedDairyId, setSelectedDiaryId] = useState<any>(
    bill?.diary_id == 0 ? null : bill?.diary_id,
  )
  const [adjustments, setAdjustments] = useState<Record<string, string>>({})
  const [useDifferentNotes, setUseDifferentNotes] = useState(false)
  const [individualNotes, setIndividualNotes] = useState<Record<string, string>>({})

  const { translate, locale } = useTranslator()

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()

  const isHebrew = locale === "he"
  const newParams = new URLSearchParams(searchParam)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const allBillItems = bill?._bill_items
  const adjustmentBillItems =
    allBillItems?.filter((item: DictType) =>
      selectedBills.some((bill) => bill == item.boq_item_id),
    ) || []

  const handleAmountChange = (billId: string, newAmount: string) => {
    setAdjustments((prev) => ({
      ...prev,
      [billId]: newAmount,
    }))
  }

  const handleAdjust = () => {
    let payload

    if (sendToApproval) {
      payload = adjustmentBillItems.map((bill: DictType) => ({
        bill_item_id: bill?.id,
        amount: adjustments[bill.id] ? adjustments[bill.id] : bill?.amount,
        delete: false,
        message_text: useDifferentNotes ? individualNotes[bill.id] || "" : reason,
      }))
    } else {
      payload = adjustmentBillItems.map((bill: DictType) => ({
        bill_item_id: bill?.id,
        amount: adjustments[bill.id] || bill.amount,
        delete: false,
        message_text: useDifferentNotes ? individualNotes[bill.id] || "" : reason,
      }))
    }

    handleEditBillMutation.mutate(payload)
  }

  const handleLinkDiary = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILL_DIARY_LINK,
      {
        bill_id: bill?.id,
        diary_id: selectedDairyId,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const linkDiaryWithBillMutation = useMutation({
    mutationKey: ["approve-bill-items"],
    mutationFn: handleLinkDiary,
    onSuccess: () => {},
    onError: (error: any) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "edit bill", translate),
        variant: "destructive",
      })
    },
  })

  const editBill = async (billItems: DictType[]) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + EDIT_BILL_ENDPOINT,
      {
        bill_id: bill?.id,
        to_approve: sendToApproval,
        items: billItems,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleEditBillMutation = useMutation({
    mutationKey: ["approve-bill-items"],
    mutationFn: editBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Bill items edited successfully"),
        variant: "success",
      })
      setSelectedItems([])
      onAdjust()
      newParams.set("revalidate", "bills")

      if (selectedDairyId) {
        linkDiaryWithBillMutation.mutate()
      }
      navigate(`?${newParams.toString()}`)
      resetForm()
      onOpenChange(false)
    },
    onError: (error: any) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "edit bill", translate),
        variant: "destructive",
      })
    },
  })

  const resetForm = () => {
    setReason("")
    setAdjustments({})
  }

  const handleCancel = () => {
    resetForm()
    onOpenChange(false)
  }

  const calculateTotals = () => {
    const originalTotal = adjustmentBillItems?.reduce((sum: any, bill: any) => {
      return (
        sum + Number.parseFloat(bill?.amount) * Number.parseFloat(bill?._boq_item?.discount_price)
      )
    }, 0)

    const newTotal = adjustmentBillItems?.reduce((sum: any, bill: any) => {
      const newAmount = adjustments[bill.id]
        ? Number.parseFloat(adjustments[bill.id]) &&
          Number.parseFloat(adjustments[bill.id]) *
            Number.parseFloat(bill?._boq_item?.discount_price)
        : Number.parseFloat(bill?.amount) * Number.parseFloat(bill?._boq_item?.discount_price)
      return sum + (isNaN(newAmount) ? 0 : newAmount)
    }, 0)

    const difference = newTotal - originalTotal
    const percentageChange = originalTotal > 0 ? (difference / originalTotal) * 100 : 0

    return { originalTotal, newTotal, difference, percentageChange }
  }

  const { originalTotal, newTotal, difference, percentageChange } = calculateTotals()

  const hasChanges = Object.keys(adjustments).some((billId) => {
    const originalAmount = Number.parseFloat(
      adjustmentBillItems?.find((b: DictType) => b.id == billId)?.amount || "0",
    )

    const newAmount = Number.parseFloat(adjustments[billId] || "0")
    return !isNaN(newAmount) && newAmount !== originalAmount
  })

  const handleIndividualNotesChange = (billId: string, newNotes: string) => {
    setIndividualNotes((prev) => ({
      ...prev,
      [billId]: newNotes,
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-full max-h-[95vh] w-full max-w-[95vw] flex-col overflow-y-auto p-4 sm:p-6 md:max-w-[90vw] lg:max-w-[85vw] xl:max-w-[80vw]",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-lg sm:text-xl'>
            {translate(sendToApproval ? "Send to approval" : "Edit Amount")}
          </DialogTitle>
          <DialogDescription className='text-sm'>
            {translate(sendToApproval ? "Bill items" : "Edit amounts for selected items")}. (
            {selectedBills.length})
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-2 sm:gap-6 sm:py-4'>
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-sm sm:text-base'>
                {translate(sendToApproval ? "Summary" : "Edit Summary")}
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className='grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-4'>
                <div>
                  <span className='text-muted-foreground'>{translate("Original Total")}:</span>
                  <p className='text-base font-medium sm:text-lg'>
                    {formatCurrency(originalTotal, isHebrew)}
                  </p>
                </div>
                <div>
                  <span className='text-muted-foreground'>{translate("New total")}:</span>
                  <p className='text-base font-medium sm:text-lg'>
                    {formatCurrency(newTotal, isHebrew)}
                  </p>
                </div>

                <div className='col-span-1 flex items-center justify-start gap-2 text-sm sm:col-span-2'>
                  <span className='text-muted-foreground'>{translate("Total Items")}:</span>
                  <Badge variant='secondary'>
                    {adjustmentBillItems?.length} {translate("items")}
                  </Badge>
                </div>
                {hasChanges && (
                  <div className='col-span-1 sm:col-span-2'>
                    <div
                      className={`flex flex-wrap items-center gap-2 text-sm ${difference >= 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      <Calculator className='h-4 w-4 flex-shrink-0' />
                      <span className='break-words'>
                        {difference >= 0
                          ? translate("Total Increase")
                          : translate("Total Decrease")}
                        :{formatCurrency(Math.abs(difference), isHebrew)} (
                        {Math.abs(percentageChange).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className='grid gap-4'>
            <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='different-notes'
                  checked={useDifferentNotes}
                  onCheckedChange={(checked) => setUseDifferentNotes(checked === true)}
                />
                <Label htmlFor='different-notes' className='text-xs font-normal sm:text-sm'>
                  {translate("Use different notes for each bill item")}
                </Label>
              </div>

              <SelectWorkDiaryList
                setSelectedDiaryId={setSelectedDiaryId}
                selectedDairyId={selectedDairyId}
              />
            </div>
            {!useDifferentNotes ? (
              <div className='grid gap-2'>
                <Label htmlFor='adjustment-reason' className='text-sm'>
                  {translate("Reason")}
                </Label>
                <Textarea
                  id='adjustment-reason'
                  placeholder={translate("Provide comment") + "..."}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className='text-sm'
                />
                <p className='text-xs text-muted-foreground'>
                  {translate("This reason will be applied to all edited bills")}.
                </p>
              </div>
            ) : (
              <div className='space-y-4'>
                <Alert>
                  <CheckCircle2 className='h-4 w-4' />
                  <AlertTitle className='text-sm'>{translate("Individual Notes")}</AlertTitle>
                  <AlertDescription className='text-xs'>
                    {translate("You can provide different approval notes for each bill")}.
                  </AlertDescription>
                </Alert>

                <div className='max-h-96 space-y-4 overflow-y-auto'>
                  {adjustmentBillItems?.map((bill: DictType) => (
                    <Card key={bill?.id}>
                      <CardHeader className='pb-3'>
                        <CardTitle className='text-sm'>{bill?._boq_item?.number}</CardTitle>
                        <p className='text-xs text-muted-foreground'>
                          {translate("Requested")} :{bill.amount}
                        </p>
                        <div className='flex flex-wrap items-center gap-2'>
                          <h4 className='text-xs text-muted-foreground'>
                            {translate("Approved")} :
                          </h4>
                          <Input
                            type='number'
                            step='0.01'
                            disabled={sendToApproval}
                            placeholder={bill?.approved_amount || bill?.amount}
                            value={adjustments[bill.id] || ""}
                            className='w-full rounded-none border-x-0 border-b border-t-0 border-b-primary shadow-none sm:w-[140px]'
                            onChange={(e) => handleAmountChange(bill.id, e.target.value)}
                          />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className='grid gap-2'>
                          <Label className='text-sm'>{translate("Approval Notes")}</Label>
                          <Textarea
                            placeholder={translate("Provide Comment")}
                            value={individualNotes[bill.id] || ""}
                            onChange={(e) => handleIndividualNotesChange(bill.id, e.target.value)}
                            rows={3}
                            className='text-sm'
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className='grid gap-2'>
            <Label className='text-sm'>{translate("Bill Amount")}</Label>
            <p className='text-xs sm:text-sm'>
              {translate(
                "You can adjust the amounts before sending for approval by updating the amount section",
              )}
              .
            </p>
            <div className='overflow-x-auto rounded-lg border'>
              <BoqTablePreview
                title={translate("Requested Amount")}
                data={adjustmentBillItems}
                handleAmountChange={handleAmountChange}
                adjustments={adjustments}
              />
            </div>
          </div>
        </div>
        <DialogFooter className='flex-col gap-2 sm:flex-row sm:gap-4'>
          <Button variant='outline' onClick={handleCancel} className='w-full sm:w-auto'>
            {translate("Cancel")}
          </Button>
          <Button
            onClick={handleAdjust}
            loading={handleEditBillMutation.isPending}
            disabled={handleEditBillMutation.isPending}
            className='w-full sm:w-auto'
          >
            <DollarSign className='mr-1 h-4 w-4' />
            {sendToApproval ? translate("Send to approve") : translate("Submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
